import React, { useEffect, useState, useLayoutEffect } from 'react'
import { Select } from '../'
import { useActivity, useInterval } from '../../hooks'
import { useTooltip } from '../../hooks'
import { EventListItem } from '../../apps/events/components'
import { ExternalEventItem } from './ExternalEventItem'

const REFRESH_INTERVAL_MS = 60000 // same cadence as the header's own notification polling

const TIMEFRAME_OPTIONS = [
  { value: 14,  label: '2 veckor' },
  { value: 30,  label: '1 månad' },
  { value: 90,  label: '3 månader' },
  { value: 180, label: '6 månader' },
]

const monthLabel = (item) => new Date(item.event_start_timestamp || item.start)
  .toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })

// Items arrive pre-sorted by date (see useActivity.js's compareItems, date
// then source app), so grouping by first-seen month key naturally preserves
// chronological month order too — no extra sort of the groups needed.
const groupByMonth = (items) => {
  const groups = new Map()
  items.forEach(item => {
    const key = monthLabel(item)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(item)
  })
  return Array.from(groups, ([label, items]) => ({ label, items }))
}

// Persistent, always-on-screen docked widget shown on wide viewports (hidden
// on narrow/portrait ones via the .activity-widget-dock CSS, see index.css) —
// renders the same EventListItem rows as the events app's own full list page,
// so the "standardised compact list" format is identical in both places.
// Collapsible via a small edge tab, mirroring NavBar's persisted show/hide
// state (localStorage key here is 'showActivityWidget' instead of 'showNavMenu').
// Named "Activity", not "Events", specifically to avoid colliding with the
// separate events app it aggregates alongside other apps' items.
//
// Also aggregates items from any other app that opts in via
// appDefinition.calendarEvents/calendarColor (see useActivity.js) — each row
// gets a colored marker bar so mixed sources stay easy to tell apart.
export const ActivityWidget = () => {
  const { ongoingItems, upcomingItems, daysAhead, setDaysAhead, refreshEvents, toggleInterest, toggleParticipation } = useActivity()
  const { showTooltip, hideTooltip } = useTooltip()

  const [showWidget, setShowWidget] = useState(true)

  useLayoutEffect(() => {
    setShowWidget(localStorage.getItem('showActivityWidget') === 'false' ? false : true)
  }, [])

  useEffect(() => {
    refreshEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAhead])

  // Keeps interest toggles, new events, etc. showing up without the user
  // having to navigate away and back to force a remount.
  useInterval(() => refreshEvents(), REFRESH_INTERVAL_MS)

  const toggleWidget = () => {
    const state = !showWidget
    setShowWidget(state)
    localStorage.setItem('showActivityWidget', state.toString())
  }

  const renderItem = (item) => (
    <div className="ev-widget-item-row" key={item.key || item.event_slug}>
      <span className="ev-widget-source-bar" style={{ background: item.sourceColor }} />
      <div className="ev-widget-item-content">
        {item.source === 'events'
          ? <EventListItem event={item} onToggleInterest={toggleInterest} />
          : <ExternalEventItem item={item} onToggleParticipation={toggleParticipation} />
        }
      </div>
    </div>
  )

  const renderMonthGroup = (group) => (
    <div className="activity-widget-month-group" key={group.label}>
      <div className="activity-widget-month-label">{group.label}</div>
      {group.items.map(renderItem)}
    </div>
  )

  return (
    <div className={"activity-widget-dock" + (showWidget ? "" : " collapsed")}>

      <div
        className="activity-widget-toggle-button"
        onClick={toggleWidget}
        onMouseEnter={() => showTooltip({ title: (showWidget ? "Minimera" : "Visa") + " events" })}
        onMouseLeave={hideTooltip}
      >
        <span>{showWidget ? '›' : '‹'}</span>
      </div>

      { showWidget &&
        <>
          <div className="activity-widget-header">
            <h2>Activity</h2>
            <Select
              name="daysAhead"
              value={daysAhead}
              options={TIMEFRAME_OPTIONS}
              onChange={({ value }) => setDaysAhead(parseInt(value, 10))}
              searchTerm="" /* keeps our own weeks-then-months order — Select
                               otherwise re-sorts options alphabetically by label */
            />
          </div>

          <div className="activity-widget-list">
            {ongoingItems.length === 0 && upcomingItems.length === 0
              ? <p className="activity-widget-empty">Inga events inom vald tidsram.</p>
              : (
                <>
                  {ongoingItems.length > 0 &&
                    <div className="activity-widget-section">
                      <div className="ev-list-section-label">Pågående</div>
                      {groupByMonth(ongoingItems).map(renderMonthGroup)}
                    </div>
                  }
                  <div className="activity-widget-section">
                    {ongoingItems.length > 0 && <div className="ev-list-section-label">Kommande</div>}
                    {groupByMonth(upcomingItems).map(renderMonthGroup)}
                  </div>
                </>
              )
            }
          </div>
        </>
      }
    </div>
  )
}
